// src/features/tuition/services/sessionsService.js
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp 
} from "firebase/firestore";
import { db, storage } from "../../../firebase/config";
import { ref, deleteObject } from "firebase/storage";

export const fetchSessions = async (tutorId, filters = {}) => {
  if (!tutorId) throw new Error("Tutor ID is required");
  
  try {
    const sessionsRef = collection(db, "tuitionSessions");
    let q = query(
      sessionsRef,
      where("tutorId", "==", tutorId),
      orderBy("date", "asc")
    );
    
    // Apply additional filters if provided
    if (filters.studentId) {
      q = query(
        sessionsRef,
        where("tutorId", "==", tutorId),
        where("studentIds", "array-contains", filters.studentId),
        orderBy("date", "asc")
      );
    }
    
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      q = query(
        sessionsRef,
        where("tutorId", "==", tutorId),
        where("date", ">=", startDate),
        where("date", "<=", endDate),
        orderBy("date", "asc")
      );
    }
    
    const querySnapshot = await getDocs(q);
    const sessions = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      sessions.push({
        id: doc.id,
        ...data,
        date: data.date?.toDate().toISOString().split('T')[0] || null,
        createdAt: data.createdAt?.toDate() || null,
        updatedAt: data.updatedAt?.toDate() || null
      });
    });
    
    return sessions;
  } catch (error) {
    console.error("Error fetching sessions:", error);
    throw error;
  }
};

export const getSession = async (sessionId) => {
  if (!sessionId) throw new Error("Session ID is required");
  
  try {
    const sessionRef = doc(db, "tuitionSessions", sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      throw new Error("Session not found");
    }
    
    const data = sessionDoc.data();
    return {
      id: sessionDoc.id,
      ...data,
      date: data.date?.toDate().toISOString().split('T')[0] || null,
      createdAt: data.createdAt?.toDate() || null,
      updatedAt: data.updatedAt?.toDate() || null
    };
  } catch (error) {
    console.error("Error getting session:", error);
    throw error;
  }
};

export const createSession = async (sessionData) => {
  try {
    // Convert date string to Firestore timestamp
    const date = new Date(sessionData.date);
    
    // Convert recurring end date if exists
    let recurringPattern = sessionData.recurringPattern;
    if (recurringPattern && recurringPattern.endDate) {
      recurringPattern = {
        ...recurringPattern,
        endDate: new Date(recurringPattern.endDate)
      };
    }
    
    const newSession = {
      ...sessionData,
      date,
      recurringPattern,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const sessionsRef = collection(db, "tuitionSessions");
    const docRef = await addDoc(sessionsRef, newSession);
    
    // Handle recurring sessions by creating multiple entries
    if (sessionData.recurring && recurringPattern.frequency !== 'none') {
      await createRecurringSessions(sessionData, docRef.id);
    }
    
    return {
      id: docRef.id,
      ...newSession,
      date: date.toISOString().split('T')[0]
    };
  } catch (error) {
    console.error("Error creating session:", error);
    throw error;
  }
};

export const updateSession = async (sessionId, sessionData) => {
  if (!sessionId) throw new Error("Session ID is required");
  
  try {
    const sessionRef = doc(db, "tuitionSessions", sessionId);
    
    // Get existing session data to check for resources that need to be deleted
    const existingSessionDoc = await getDoc(sessionRef);
    if (existingSessionDoc.exists()) {
      const existingData = existingSessionDoc.data();
      
      // Check for resources that were removed and delete their files from storage
      if (existingData.resources && Array.isArray(existingData.resources)) {
        const existingResourcePaths = existingData.resources
          .filter(r => r.storagePath)
          .map(r => r.storagePath);
          
        const newResourcePaths = sessionData.resources
          .filter(r => r.storagePath)
          .map(r => r.storagePath);
          
        // Find paths that exist in the old data but not in the new data
        const pathsToDelete = existingResourcePaths.filter(
          path => !newResourcePaths.includes(path)
        );
        
        // Delete those storage items
        await Promise.all(
          pathsToDelete.map(async (path) => {
            try {
              const fileRef = ref(storage, path);
              await deleteObject(fileRef);
            } catch (error) {
              console.warn(`Failed to delete file at path ${path}:`, error);
            }
          })
        );
      }
    }
    
    // Convert date string to Firestore timestamp
    const date = new Date(sessionData.date);
    
    // Convert recurring end date if exists
    let recurringPattern = sessionData.recurringPattern;
    if (recurringPattern && recurringPattern.endDate) {
      recurringPattern = {
        ...recurringPattern,
        endDate: new Date(recurringPattern.endDate)
      };
    }
    
    // Remove fields that shouldn't be updated directly
    const { id, createdAt, ...updateData } = sessionData;
    
    await updateDoc(sessionRef, {
      ...updateData,
      date,
      recurringPattern,
      updatedAt: serverTimestamp()
    });
    
    return {
      id: sessionId,
      ...sessionData,
      date: date.toISOString().split('T')[0],
      updatedAt: new Date()
    };
  } catch (error) {
    console.error("Error updating session:", error);
    throw error;
  }
};

export const deleteSession = async (sessionId) => {
  if (!sessionId) throw new Error("Session ID is required");
  
  try {
    // First, get the session to check for resources
    const sessionDoc = await getDoc(doc(db, "tuitionSessions", sessionId));
    
    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();
      
      // Delete any attached resources from storage
      if (sessionData.resources && Array.isArray(sessionData.resources)) {
        await Promise.all(
          sessionData.resources
            .filter(resource => resource.storagePath)
            .map(async (resource) => {
              try {
                const fileRef = ref(storage, resource.storagePath);
                await deleteObject(fileRef);
              } catch (error) {
                console.warn(`Failed to delete file for resource "${resource.name}":`, error);
              }
            })
        );
      }
    }
    
    // Now delete the session document
    await deleteDoc(doc(db, "tuitionSessions", sessionId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting session:", error);
    throw error;
  }
};

export const updateSessionStatus = async (sessionId, status) => {
  if (!sessionId) throw new Error("Session ID is required");
  
  try {
    const sessionRef = doc(db, "tuitionSessions", sessionId);
    
    await updateDoc(sessionRef, {
      status,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, id: sessionId, status };
  } catch (error) {
    console.error("Error updating session status:", error);
    throw error;
  }
};

// Helper function to create recurring sessions
const createRecurringSessions = async (templateSession, parentSessionId) => {
  try {
    const {
      recurring,
      recurringPattern,
      date: dateStr,
      ...sessionTemplate
    } = templateSession;
    
    if (!recurring || recurringPattern.frequency === 'none') {
      return [];
    }
    
    const startDate = new Date(dateStr);
    const endDate = new Date(recurringPattern.endDate);
    const currentDate = new Date(startDate);
    currentDate.setDate(currentDate.getDate() + 1); // Start from next day
    
    const sessions = [];
    const sessionsRef = collection(db, "tuitionSessions");
    
    while (currentDate <= endDate) {
      let shouldCreateSession = false;
      
      switch (recurringPattern.frequency) {
        case 'daily':
          shouldCreateSession = true;
          break;
        case 'weekly':
          const dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
          shouldCreateSession = recurringPattern.daysOfWeek.includes(dayOfWeek);
          break;
        case 'biweekly':
          const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
          const weekDiff = Math.round((currentDate - startDate) / (14 * 24 * 60 * 60 * 1000));
          shouldCreateSession = recurringPattern.daysOfWeek.includes(dayName) && weekDiff % 2 === 0;
          break;
        case 'monthly':
          const dayOfMonth = startDate.getDate();
          shouldCreateSession = currentDate.getDate() === dayOfMonth;
          break;
      }
      
      if (shouldCreateSession) {
        const newSession = {
          ...sessionTemplate,
          date: new Date(currentDate),
          recurring: false, // Child sessions aren't recurring themselves
          parentSessionId, // Reference to the parent recurring session
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        const docRef = await addDoc(sessionsRef, newSession);
        sessions.push({ id: docRef.id, ...newSession });
      }
      
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return sessions;
  } catch (error) {
    console.error("Error creating recurring sessions:", error);
    throw error;
  }
};